import { captureError } from "./sentryHelper";

export enum SoundType {
	Notification,
	Tone,
	Busy,
	Ringing
}

export const SoundTypeDuration = {
	Notification: 2000,
	Tone: 300,
	Busy: 5000,
	Default: 1000,
};

export default class PlaySoundHelper {
	private static IsPlaying: boolean = false;
	private static AudioLoopInProgress: boolean = false;
	private static CurrentPlayingAudioLoop: HTMLAudioElement | null = null;
	private static CurrentPlayingSoundType: SoundType | null = null;

	private static readonly NotificationSound = new Audio(
		"./assets/audio/notification-ping.mp3"
	);
	private static readonly ToneSound = new Audio(
		"./assets/audio/send-dtmf-tone.wav"
	);

	public static playSound = (
		soundType: SoundType,
		path: string | undefined = undefined,
		loop: boolean = false
	): HTMLAudioElement | undefined => {
		try {
			const initialDelay = soundType === SoundType.Busy ? 800 : 0;
			const audioFile = this.getAudioFile(soundType, path);

			if (loop) {
				if (PlaySoundHelper.AudioLoopInProgress) {
					return;
				}

				PlaySoundHelper.AudioLoopInProgress = true;
			}

			if (!audioFile) {
				console.error("Audio file missing");

				return undefined;
			}

			audioFile.loop = loop;
			const duration = this.getSoundDuration(soundType);

			//cancel playing current sound before playing the new one
			const isDifferentSoundAlreadyPlaying = PlaySoundHelper.IsPlaying && PlaySoundHelper.CurrentPlayingSoundType && PlaySoundHelper.CurrentPlayingSoundType != soundType;

			if (isDifferentSoundAlreadyPlaying) {
				PlaySoundHelper.IsPlaying = false;
				PlaySoundHelper.CurrentPlayingSoundType = null;
				PlaySoundHelper.stopPlayingLoop();
			}

			setTimeout(() => {
				if (PlaySoundHelper.IsPlaying || !audioFile) {
					return;
				}

				PlaySoundHelper.IsPlaying = true;
				PlaySoundHelper.CurrentPlayingSoundType = soundType;
				audioFile.play();

				setTimeout(() => {
					PlaySoundHelper.IsPlaying = false;
					PlaySoundHelper.CurrentPlayingSoundType = null;
				}, duration);
			}, initialDelay);

			if (loop) {
				PlaySoundHelper.CurrentPlayingAudioLoop = audioFile;
			}

			return audioFile;
		} catch (error: any) {
			if (error.name === "NotAllowedError") {
				console.warn(
					"NotAllowedError: User interaction is required to play the audio."
				);
			} else {
				captureError(error, "Play sound failed", undefined, {
					soundType,
					path,
					loop,
				});
			}

			return;
		}
	};

	public static stopPlayingLoop = () => {
		if (PlaySoundHelper.CurrentPlayingAudioLoop) {
			PlaySoundHelper.CurrentPlayingAudioLoop.pause();
			PlaySoundHelper.AudioLoopInProgress = false;
		}
	};

	private static readonly getSoundDuration = (soundType: SoundType) => {
		switch (soundType) {
			case SoundType.Notification:
				return SoundTypeDuration.Notification;
			case SoundType.Tone:
				return SoundTypeDuration.Tone;
			case SoundType.Busy:
				return SoundTypeDuration.Busy;
			default:
				return SoundTypeDuration.Default;
		}
	};

	private static readonly getAudioFile = (
		soundType: SoundType,
		path: string | undefined = undefined
	) => {
		switch (soundType) {
			case SoundType.Notification:
				return PlaySoundHelper.NotificationSound;
			case SoundType.Tone:
				return PlaySoundHelper.ToneSound;
			case SoundType.Busy:
			case SoundType.Ringing:
				return new Audio(path);
			default:
				return null;
		}
	};
}
